import * as THREE from "three";
import { getViewerState } from "../../stores/viewerStore";
import { computeLargestRobotDimension } from "../computeLargestRobotDimension";
import {
  computeInertialVisualScale,
  resolveInertialPrincipalAxes,
} from "../../utils/inertiaTensor";

const INERTIAL_SPHERE_GEOMETRY = new THREE.SphereGeometry(1);

export function attachInertial({ bodyNode, poseGroup, showInertials, sceneRef }) {
  // Add inertia ellipsoids
  if (bodyNode.inertia) {
    const inertial = bodyNode.inertia;
    const inertialGroup = new THREE.Group();
    inertialGroup.userData = {
      ...(inertialGroup.userData || {}),
      type: "inertial",
      bodyName: bodyNode.name,
      bodyUuid: bodyNode.uuid,
      inertialUuid: inertial.uuid,
      inertialName: inertial.name || null,
      selectable: true,
    };

    // Set position and rotation from inertia data (support both old and new property names)
    const position = inertial.position || inertial.pos || [0, 0, 0];
    const orientation = inertial.orientation || inertial.quat || [1, 0, 0, 0];
    inertialGroup.position.set(...position);
    inertialGroup.setRotationFromQuaternion(
      new THREE.Quaternion(orientation[1], orientation[2], orientation[3], orientation[0])
    );

    // Create ellipsoid geometry from diagonal inertia or principal axes of full inertia.
    const principalAxes = resolveInertialPrincipalAxes(inertial);
    if (principalAxes) {
      const { diagonal, orientation: principalOrientation } = principalAxes;
      const mass = inertial.mass || 1.0;

      // Calculate color based on mass (blue for low mass, red for high mass)
      // Get mass range from viewer store (visualization-only)
      const massRange = getViewerState().inertiaMassRange;
      let color = 0x00ff00; // Default green

      if (
        massRange &&
        massRange[0] !== null &&
        massRange[1] !== null &&
        massRange[0] !== massRange[1]
      ) {
        // Interpolate between blue (#2196f3) and red (#f44336) based on mass
        const normalizedMass = Math.max(
          0,
          Math.min(1, (mass - massRange[0]) / (massRange[1] - massRange[0]))
        );

        // Color interpolation: blue to red
        const blueColor = new THREE.Color(0x2196f3); // Blue
        const redColor = new THREE.Color(0xf44336); // Red
        color = blueColor.clone().lerp(redColor, normalizedMass).getHex();
      }

      const material = new THREE.MeshBasicMaterial({
        color: color,
        transparent: true,
        opacity: 0.35,
        side: THREE.DoubleSide,
        wireframe: true,
      });

      const ellipsoid = new THREE.Mesh(INERTIAL_SPHERE_GEOMETRY, material);

      const modelGroupForScale = sceneRef.current?.getObjectByName("modelGroup");
      // Match joint markers: per-root robot extent so multiple spaced-out robots
      // do not share one huge bbox (which made every inertial ellipsoid enormous).
      const largestDim =
        modelGroupForScale != null ? computeLargestRobotDimension(modelGroupForScale) : 0;
      const [scaleX, scaleY, scaleZ] = computeInertialVisualScale(diagonal, mass, largestDim);

      ellipsoid.scale.set(scaleX, scaleY, scaleZ);
      ellipsoid.setRotationFromQuaternion(principalOrientation);
      ellipsoid.visible = showInertials;
      ellipsoid.userData = {
        ...ellipsoid.userData,
        type: "inertial",
        bodyName: bodyNode.name,
        bodyUuid: bodyNode.uuid,
        inertialUuid: inertial.uuid,
        inertialName: inertial.name || null,
        mass: mass,
        diagonal: diagonal,
        selectable: true,
      };

      // Add an invisible solid mesh for better raycasting (wireframe can be hard to click)
      // This helper is always "visible" for raycasting but rendered as invisible
      const helperMaterial = new THREE.MeshBasicMaterial({
        transparent: true,
        opacity: 0,
        colorWrite: false,
        depthWrite: false,
        side: THREE.DoubleSide,
      });
      const helperMesh = new THREE.Mesh(INERTIAL_SPHERE_GEOMETRY, helperMaterial);
      helperMesh.scale.set(scaleX, scaleY, scaleZ);
      helperMesh.setRotationFromQuaternion(principalOrientation);
      helperMesh.visible = true; // Always visible for raycasting
      helperMesh.renderOrder = -1; // Render behind wireframe
      helperMesh.userData = {
        ...ellipsoid.userData,
        isInertialHelper: true,
      };

      inertialGroup.add(ellipsoid);
      inertialGroup.add(helperMesh);
    }

    poseGroup.add(inertialGroup);
  }
}
